import React, { useEffect, useState } from "react";
import { CreditCard, ExternalLink, Link2, Loader2, RefreshCw, ShieldCheck, Unplug, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function TelemacoConnection() {
    const [status, setStatus] = useState(null);
    const [balance, setBalance] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [statusResponse, balanceResponse] = await Promise.all([
                api.get("/infocamere/telemaco/status"),
                api.get("/infocamere/telemaco/balance"),
            ]);
            setStatus(statusResponse.data);
            setBalance(balanceResponse.data);
        }
        catch (e) { toast.error(e?.response?.data?.detail || "Errore stato Telemaco"); }
    };
    useEffect(() => { load(); }, []);

    const connect = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/infocamere/telemaco/connect");
            window.location.assign(data.authorization_url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Collegamento non disponibile");
        } finally { setBusy(false); }
    };

    const disconnect = async () => {
        if (!window.confirm("Scollegare l'Utente Telemaco da questo affiliato?")) return;
        setBusy(true);
        try { await api.delete("/infocamere/telemaco/connection"); await load(); toast.success("Telemaco scollegato"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Errore scollegamento"); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-3xl mx-auto p-6 lg:p-10">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-primary font-bold mb-2">Account affiliato</div>
                <h1 className="font-display text-3xl font-medium">Collegamento Telemaco</h1>
                <p className="text-sm text-muted-foreground mt-2">Ogni affiliato utilizza esclusivamente il proprio Utente Telemaco.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 space-y-5">
                <div className="flex items-start gap-3">
                    <ShieldCheck className="text-primary mt-0.5" size={22} />
                    <div>
                        <div className="font-semibold">La password non viene salvata da ComUnica Lab</div>
                        <p className="text-sm text-muted-foreground mt-1">L'accesso avverrà sulla pagina ufficiale InfoCamere tramite OAuth-PKCE. Token e sessione saranno cifrati e isolati per affiliato.</p>
                    </div>
                </div>
                {!status ? <Loader2 className="animate-spin" /> : status.connected ? (
                    <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                        <div className="font-semibold text-green-800">Telemaco collegato</div>
                        <div className="text-xs text-green-700 mt-1">Ambiente: {status.environment} · Utente: {status.telemaco_subject || "identificato da InfoCamere"}</div>
                    </div>
                ) : (
                    <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
                        <div className="font-semibold text-orange-800">Non collegato</div>
                        <div className="text-xs text-orange-700 mt-1">Il pulsante sarà operativo dopo l'abilitazione al collaudo da parte di InfoCamere.</div>
                    </div>
                )}
                <div className="flex flex-wrap gap-3">
                    {!status?.connected ? (
                        <button onClick={connect} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white font-medium disabled:opacity-50">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />} Collega Utente Telemaco <ExternalLink size={14} />
                        </button>
                    ) : (
                        <button onClick={disconnect} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-destructive text-destructive font-medium disabled:opacity-50">
                            <Unplug size={16} /> Scollega
                        </button>
                    )}
                </div>
            </div>
            <div className="mt-6 rounded-xl border border-border bg-card p-6">
                <div className="flex items-center gap-2 mb-4">
                    <WalletCards size={20} className="text-primary" />
                    <h2 className="font-display text-xl font-medium">Disponibilità Telemaco</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <BalanceCard title="Diritti" subtitle="Diritti di segreteria e imposta di bollo" value={balance?.rights_balance} />
                    <BalanceCard title="Tariffe" subtitle="Servizi telematici e documenti" value={balance?.tariffs_balance} />
                </div>
                <p className="text-xs text-muted-foreground mt-4">{balance?.message || "Caricamento informazioni conto..."}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                    <button disabled={!balance?.balance_api_available || busy} onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm font-medium disabled:opacity-40" title="Disponibile solo se InfoCamere abilita un'API saldo">
                        <RefreshCw size={15} /> Aggiorna saldo
                    </button>
                    <a href={balance?.recharge_url || "https://registroimprese.infocamere.it/area-utente"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium">
                        <CreditCard size={15} /> Vai alla ricarica Telemaco <ExternalLink size={13} />
                    </a>
                </div>
                <div className="mt-4 text-[11px] text-muted-foreground">La ricarica e gli addebiti camerali restano esterni e distinti dai crediti ComUnica Lab.</div>
            </div>
        </div>
    );
}

function BalanceCard({ title, subtitle, value }) {
    return (
        <div className="rounded-lg border border-border bg-background-alt p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{title}</div>
            <div className="text-2xl font-semibold mt-1">{typeof value === "number" ? `€ ${value.toFixed(2)}` : "Non disponibile"}</div>
            <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
        </div>
    );
}
